import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Input, Select, InputNumber, Spin, Empty } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiProductosInstance, authHeaderProductos } from "../../redux/actions/productos/productos";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";

import AjustesInventario from "../../components/devoluciones/AjustesInventario";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const money = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ACTION_CONFIG = {
  CREATE: { label: "Creacion", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
  DELETE: { label: "Eliminacion", color: "#b91c1c", bg: "#fee2e2" },
};
const getActionConfig = (action) => ACTION_CONFIG[action] || { label: action || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function ProductoDetallePage() {
  const { idProducto } = useParams();
  const navigate = useNavigate();

  const [producto, setProducto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);

  const [addVarianteOpen, setAddVarianteOpen] = useState(false);
  const [savingVariante, setSavingVariante] = useState(false);
  const [nuevaVariante, setNuevaVariante] = useState({});

  const fetchProducto = useCallback(() => {
    setLoading(true);
    apiProductosInstance.get(`/productos/${idProducto}`, { headers: authHeaderProductos() })
      .then((res) => setProducto(res.data))
      .catch(() => toast.error("No se pudo cargar el producto"))
      .finally(() => setLoading(false));
  }, [idProducto]);

  useEffect(() => { fetchProducto(); }, [fetchProducto]);

  useEffect(() => {
    const headers = authHeaderProductos();
    apiProductosInstance.get("/categorias", { headers }).then((r) => setCategorias((r.data?.items ?? []).map((c) => ({ label: c.nombre, value: c.id_categoria })))).catch(() => {});
    apiProductosInstance.get("/unidades-medida", { headers }).then((r) => setUnidades((r.data?.items ?? []).map((u) => ({ label: u.nombre, value: u.id_unidad_medida })))).catch(() => {});
  }, []);

  useEffect(() => {
    if (editForm.id_categoria == null) { setSubcategorias([]); return; }
    apiProductosInstance.get("/subcategorias", { headers: authHeaderProductos(), params: { id_categoria: editForm.id_categoria } })
      .then((r) => setSubcategorias((r.data?.items ?? []).map((s) => ({ label: s.nombre, value: s.id_subcategoria }))))
      .catch(() => {});
  }, [editForm.id_categoria]);

  useEffect(() => {
    if (activeTab !== "actividad" || !idProducto) return;
    setLoadingActividad(true);
    apiProductosInstance.get(`/productos/${idProducto}/actividad`, { headers: authHeaderProductos() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idProducto]);

  const startEditInfo = () => {
    setEditForm({
      nombre: producto.nombre,
      artista: producto.artista || "",
      id_categoria: producto.id_categoria,
      id_subcategoria: producto.id_subcategoria,
      tasa_iva: producto.tasa_iva != null ? parseFloat(producto.tasa_iva) : undefined,
      descripcion: producto.descripcion || "",
    });
    setEditingInfo(true);
  };

  const cancelEditInfo = () => setEditingInfo(false);

  const handleSaveInfo = async () => {
    if (!editForm.nombre?.trim() || !editForm.id_categoria) {
      toast.warning("Nombre y categoria son obligatorios");
      return;
    }
    setSavingInfo(true);
    try {
      const payload = {
        nombre: editForm.nombre.trim(),
        id_categoria: editForm.id_categoria,
        id_subcategoria: editForm.id_subcategoria || null,
        artista: editForm.artista?.trim() || null,
        descripcion: editForm.descripcion?.trim() || null,
        tasa_iva: editForm.tasa_iva ?? null,
        variantes: (producto.variantes || []).map((v) => ({
          id_variante: v.id_variante,
          clave: v.clave,
          descripcion_variante: v.descripcion_variante,
          id_unidad_medida: v.id_unidad_medida,
          precio_venta: parseFloat(v.precio_venta),
          costo_promedio: v.costo_promedio != null ? parseFloat(v.costo_promedio) : null,
          costo_conocido: Boolean(v.costo_conocido),
          existencia: parseFloat(v.existencia),
          stock_minimo: v.stock_minimo != null ? parseFloat(v.stock_minimo) : null,
        })),
      };
      await apiProductosInstance.patch(`/productos/${idProducto}`, payload, { headers: authHeaderProductos() });
      toast.success("Producto actualizado correctamente");
      setEditingInfo(false);
      fetchProducto();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "Eliminar este producto?",
      content: "Esta accion desactiva el producto y todas sus variantes. No se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiProductosInstance.delete(`/productos/${idProducto}`, { headers: authHeaderProductos() });
          toast.success("Producto eliminado");
          navigate("/app/productos");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleAddVariante = async () => {
    if (!nuevaVariante.clave || !nuevaVariante.id_unidad_medida || nuevaVariante.precio_venta == null) {
      toast.warning("Clave, unidad y precio son obligatorios");
      return;
    }
    setSavingVariante(true);
    try {
      const payload = {
        nombre: producto.nombre,
        id_categoria: producto.id_categoria,
        id_subcategoria: producto.id_subcategoria,
        artista: producto.artista,
        descripcion: producto.descripcion,
        tasa_iva: producto.tasa_iva,
        variantes: [
          ...(producto.variantes || []).map((v) => ({
            id_variante: v.id_variante,
            clave: v.clave,
            descripcion_variante: v.descripcion_variante,
            id_unidad_medida: v.id_unidad_medida,
            precio_venta: parseFloat(v.precio_venta),
            costo_promedio: v.costo_promedio,
            costo_conocido: Boolean(v.costo_conocido),
            existencia: parseFloat(v.existencia),
            stock_minimo: v.stock_minimo,
          })),
          {
            clave: nuevaVariante.clave,
            descripcion_variante: nuevaVariante.descripcion_variante || null,
            id_unidad_medida: nuevaVariante.id_unidad_medida,
            precio_venta: nuevaVariante.precio_venta,
            costo_promedio: nuevaVariante.costo_promedio ?? null,
            costo_conocido: false,
            existencia: nuevaVariante.existencia ?? 0,
            stock_minimo: nuevaVariante.stock_minimo ?? null,
          },
        ],
      };
      await apiProductosInstance.patch(`/productos/${idProducto}`, payload, { headers: authHeaderProductos() });
      toast.success("Variante agregada");
      setAddVarianteOpen(false);
      setNuevaVariante({});
      fetchProducto();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar la variante");
    } finally {
      setSavingVariante(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!producto) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Producto no encontrado" />
      </div>
    );
  }

  const variantes = producto.variantes || [];
  const existenciaTotal = variantes.reduce((a, v) => a + parseFloat(v.existencia || 0), 0);
  const precioDesde = variantes.length ? Math.min(...variantes.map((v) => parseFloat(v.precio_venta))) : null;

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">{(producto.nombre || "—").toUpperCase()}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item">
                  <i className="fa-solid fa-calendar" />
                  Creado el {fmtFechaCorta(producto.datetime)}
                </span>
                {producto.creado_por && (
                  <span className="cd-meta-item">
                    <i className="fa-solid fa-user" />
                    {capitalize(producto.creado_por)}
                  </span>
                )}
              </div>
            </div>

            <div className="cd-header-actions">
              <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "variantes" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("variantes")}>
              <i className="fa-solid fa-list" /> Variantes ({variantes.length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "ajustes" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("ajustes")}>
              <i className="fa-solid fa-boxes-stacked" /> Ajustes de inventario
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="cd-card-icon-wrap"><i className="fa-solid fa-compact-disc"></i></div>
                  <h2 className="cd-card-title">Informacion del producto</h2>
                </div>
                {!editingInfo && (
                  <Button icon={<i className="fa-solid fa-pen" />} className="cd-btn-edit" onClick={startEditInfo}>
                    Editar
                  </Button>
                )}
              </div>

              {editingInfo ? (
                <div className="cd-edit-form">
                  <div className="cd-edit-grid">
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Nombre</span>
                      <Input value={editForm.nombre} onChange={(e) => setEditForm((f) => ({ ...f, nombre: e.target.value }))} />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Artista</span>
                      <Input value={editForm.artista} onChange={(e) => setEditForm((f) => ({ ...f, artista: e.target.value }))} placeholder="Opcional" />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Categoria</span>
                      <Select style={{ width: "100%" }} showSearch optionFilterProp="label" options={categorias} value={editForm.id_categoria} onChange={(v) => setEditForm((f) => ({ ...f, id_categoria: v }))} />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Subcategoria</span>
                      <Select style={{ width: "100%" }} allowClear showSearch optionFilterProp="label" options={subcategorias} value={editForm.id_subcategoria} onChange={(v) => setEditForm((f) => ({ ...f, id_subcategoria: v }))} />
                    </div>
                    <div className="cd-edit-field">
                      <span className="cd-edit-label">Tasa de IVA %</span>
                      <InputNumber style={{ width: "100%" }} min={0} max={100} precision={2} value={editForm.tasa_iva} onChange={(v) => setEditForm((f) => ({ ...f, tasa_iva: v }))} />
                    </div>
                    <div className="cd-edit-field" style={{ gridColumn: "1 / -1" }}>
                      <span className="cd-edit-label">Descripcion</span>
                      <Input.TextArea rows={3} value={editForm.descripcion} onChange={(e) => setEditForm((f) => ({ ...f, descripcion: e.target.value }))} />
                    </div>
                  </div>
                  <div className="cd-edit-actions">
                    <Button icon={<CloseOutlined />} onClick={cancelEditInfo} disabled={savingInfo}>Cancelar</Button>
                    <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                      Guardar
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                  <div>
                    <span className="cd-field-label">Nombre</span>
                    <span className="cd-field-value">{capitalize(producto.nombre) || "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Artista</span>
                    <span className="cd-field-value">{producto.artista || "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Categoria</span>
                    <span className="cd-field-value">{producto.categoria_nombre || "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Subcategoria</span>
                    <span className="cd-field-value">{producto.subcategoria_nombre || "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Tasa de IVA</span>
                    <span className="cd-field-value">{producto.tasa_iva != null ? `${producto.tasa_iva}%` : "—"}</span>
                  </div>
                  <div>
                    <span className="cd-field-label">Fecha de creacion</span>
                    <span className="cd-field-value">{fmtFechaCorta(producto.datetime)}</span>
                  </div>
                  {producto.descripcion && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <span className="cd-field-label">Descripcion</span>
                      <span className="cd-field-value" style={{ fontWeight: 400, color: "#595c5e", lineHeight: 1.5 }}>
                        {producto.descripcion}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-chart-simple"></i></div>
                <h2 className="cd-card-title">Resumen</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div>
                  <span className="cd-field-label">Variantes activas</span>
                  <span className="cd-field-value">{variantes.length}</span>
                </div>
                <div>
                  <span className="cd-field-label">Existencia total</span>
                  <span className="cd-field-value">{existenciaTotal}</span>
                </div>
                <div>
                  <span className="cd-field-label">Precio desde</span>
                  <span className="cd-field-value">{money(precioDesde)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "variantes" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-list"></i></div>
                <h2 className="cd-card-title">Variantes</h2>
              </div>
              <Button type="primary" icon={<i className="fa-solid fa-plus" />} onClick={() => setAddVarianteOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                Agregar variante
              </Button>
            </div>

            {variantes.length === 0 ? (
              <Empty description="Este producto todavia no tiene variantes" />
            ) : (
              <div className="cd-pagos-list">
                {variantes.map((v) => (
                  <div key={v.id_variante} className="cd-pagos-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2" }}>
                    <div><span className="cd-field-label">Clave</span><div className="cd-field-value">{v.clave}</div></div>
                    <div><span className="cd-field-label">Descripcion</span><div className="cd-field-value">{v.descripcion_variante || "—"}</div></div>
                    <div><span className="cd-field-label">Unidad</span><div className="cd-field-value">{v.unidad_nombre || "—"}</div></div>
                    <div><span className="cd-field-label">Precio</span><div className="cd-field-value">{money(v.precio_venta)}</div></div>
                    <div><span className="cd-field-label">Existencia</span><div className="cd-field-value">{v.existencia}</div></div>
                    <div><span className="cd-field-label">Stock minimo</span><div className="cd-field-value">{v.stock_minimo ?? "—"}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "ajustes" && <AjustesInventario producto={producto} onChanged={fetchProducto} />}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? (
              <Spin />
            ) : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                          {item.user_nombre && <span className="cd-actividad-user">{item.user_nombre}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={addVarianteOpen}
        onCancel={() => setAddVarianteOpen(false)}
        onOk={handleAddVariante}
        okText="Agregar"
        cancelText="Cancelar"
        title="Agregar variante"
        confirmLoading={savingVariante}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Input placeholder="Clave (SKU)" value={nuevaVariante.clave || ""} onChange={(e) => setNuevaVariante((f) => ({ ...f, clave: e.target.value }))} />
          <Input placeholder="Descripcion (opcional)" value={nuevaVariante.descripcion_variante || ""} onChange={(e) => setNuevaVariante((f) => ({ ...f, descripcion_variante: e.target.value }))} />
          <Select placeholder="Unidad de medida" options={unidades} value={nuevaVariante.id_unidad_medida} onChange={(v) => setNuevaVariante((f) => ({ ...f, id_unidad_medida: v }))} showSearch optionFilterProp="label" />
          <InputNumber style={{ width: "100%" }} placeholder="Precio de venta" min={0} precision={2} value={nuevaVariante.precio_venta} onChange={(v) => setNuevaVariante((f) => ({ ...f, precio_venta: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Existencia inicial" min={0} precision={3} value={nuevaVariante.existencia} onChange={(v) => setNuevaVariante((f) => ({ ...f, existencia: v }))} />
          <InputNumber style={{ width: "100%" }} placeholder="Stock minimo (opcional)" min={0} precision={3} value={nuevaVariante.stock_minimo} onChange={(v) => setNuevaVariante((f) => ({ ...f, stock_minimo: v }))} />
        </div>
      </Modal>
    </div>
  );
}
